/**
 * MyPhoneNumberCard
 *
 * Self-service surface for a STAFF user to register and verify their OWN
 * (non-ported) phone number on web — the web parity of the mobile
 * "My phone number" screen. Mirrors the admin BYO flow in
 * PhoneNumberManagement, but scoped to the signed-in user.
 *
 * Flow:
 *   1. Enter own number in E.164.
 *   2. POST /api/user-phone-numbers with requires_verification=true → the row
 *      is created 'unverified' (NOT auto-verified like a platform number).
 *   3. VerifyNumberModal runs Verified Caller ID (voice) or SMS OTP so the
 *      resolver will send from it — no porting required.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Phone, Plus, Loader2, ShieldCheck, X } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { VerifyNumberModal } from '@/components/settings/VerifyNumberModal';
import { useTabbedChrome } from '@/components/weavein';

interface MyNumber {
  id: string;
  phone_number: string;
  is_active: boolean;
  is_primary: boolean;
  verification_status?: string;
  verification_method?: string | null;
  provider_type?: string | null;
}

interface MyPhoneNumberCardProps {
  userId: number;
  organizationId: number;
}

const formatPhone = (p: string) => {
  const d = (p || '').replace(/\D/g, '');
  const n = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return n.length === 10 ? `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}` : (p || '');
};

export const MyPhoneNumberCard: React.FC<MyPhoneNumberCardProps> = ({ userId, organizationId }) => {
  const tabbed = useTabbedChrome();
  const [numbers, setNumbers] = useState<MyNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState<MyNumber | null>(null);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'X-CBO-ID': String(organizationId || ''),
  }), [organizationId]);

  const fetchNumbers = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/user-phone-numbers/user/${userId}`), { headers: headers() });
      if (res.ok) {
        const data = await res.json().catch(() => []);
        setNumbers(Array.isArray(data) ? data : []);
      }
    } catch {
      setError('Could not load your numbers');
    } finally {
      setLoading(false);
    }
  }, [userId, headers]);

  useEffect(() => { fetchNumbers(); }, [fetchNumbers]);

  const addNumber = async () => {
    const phone = newPhone.trim();
    if (!phone || !userId || !organizationId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(getWeaveinApiUrl('/api/user-phone-numbers'), {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          user_id: userId,
          organization_id: organizationId,
          phone_number: phone,
          phone_number_type: 'local',
          provider_type: 'twilio',
          sms_enabled: true,
          voice_enabled: true,
          is_primary: numbers.length === 0,
          // Own number — must prove ownership before the resolver sends from it.
          requires_verification: true,
        }),
      });
      if (!res.ok) {
        let detail = `Could not add number (HTTP ${res.status})`;
        try { const b = await res.json(); if (b?.detail) detail = b.detail; } catch { /* not JSON */ }
        setError(detail);
        return;
      }
      const created = await res.json().catch(() => ({}));
      const newId = created?.phone_number_id != null ? String(created.phone_number_id) : null;
      setShowAdd(false);
      setNewPhone('');
      await fetchNumbers();
      if (newId) {
        setVerifyTarget({ id: newId, phone_number: phone, is_active: true, is_primary: numbers.length === 0, verification_status: 'unverified' });
      }
    } catch {
      setError('Could not add number');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6">
      {!tabbed && (
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/40">
            <Phone className="w-5 h-5 text-purple-600 dark:text-purple-300" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">My Phone Number</h1>
        </div>
      )}
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Use your own number for calls and texts without porting it. Add it below and verify
        ownership — outbound calls will then show your number as the caller ID.
      </p>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-3">
          {numbers.length === 0 ? (
            <div className="text-center py-10 text-gray-500 border border-dashed rounded-lg dark:border-gray-700">
              <Phone className="w-9 h-9 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No number added yet</p>
              <p className="text-sm mt-1">Add your own number to send calls and texts from it.</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg divide-y dark:divide-gray-700">
              {numbers.map(n => (
                <div key={n.id} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <span className="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">{formatPhone(n.phone_number)}</span>
                    {n.is_primary && <span className="ml-2 text-xs text-green-600 font-medium">Primary</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {n.verification_status === 'verified' ? (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">Verified</span>
                    ) : n.verification_status === 'pending' ? (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Pending</span>
                    ) : (
                      <button
                        onClick={() => setVerifyTarget(n)}
                        className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                      >
                        <ShieldCheck className="w-3.5 h-3.5" /> Verify
                      </button>
                    )}
                    <span className={`text-xs px-1.5 py-0.5 rounded ${n.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {n.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showAdd ? (
            <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Your phone number</label>
                <button onClick={() => { setShowAdd(false); setNewPhone(''); }} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><X className="w-4 h-4" /></button>
              </div>
              <input
                type="tel" value={newPhone}
                onChange={e => setNewPhone(e.target.value)}
                placeholder="+15551234567"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">Enter in E.164 format (+1 then 10 digits). We&apos;ll verify ownership next.</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => { setShowAdd(false); setNewPhone(''); }} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
                <button onClick={addNumber} disabled={!newPhone.trim() || saving}
                  className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add &amp; Verify
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAdd(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium">
              <Plus className="w-4 h-4" /> Add my number
            </button>
          )}
        </div>
      )}

      {verifyTarget && organizationId && (
        <VerifyNumberModal
          phoneNumberId={verifyTarget.id}
          phoneNumber={formatPhone(verifyTarget.phone_number)}
          organizationId={organizationId}
          onClose={() => setVerifyTarget(null)}
          onVerified={() => { fetchNumbers(); }}
        />
      )}
    </div>
  );
};

export default MyPhoneNumberCard;
